/**
 * §13.1.1 Title. Centre column: wordmark over the manta sigil, vertical menu,
 * version strip. Focus order Descend → Resume? → Archive → Settings → Quit.
 *
 * AMD-49 put Archive between Resume and Settings. It belongs above Settings because it
 * is about the game rather than about the machine, and below Descend and Resume because
 * neither of those may ever grow a step: the fastest path from boot to playing is the
 * one thing a title screen owes you.
 *
 * The sigil is an ASCII render of the player manta silhouette (§12), a nod to the
 * canvasui ascii-object treatment done with plain text so it costs nothing.
 */

import { button, div, setHidden } from '../dom';
import { Screen, type ActionSink } from './base';

/** Manta sigil, 20% alpha behind the wordmark (§13.1.1). */
const SIGIL = [
  '        .:*#%%#*:.        ',
  '     :*%@@@@@@@@@%*:     ',
  '   .*%@@#=-::::-=#@@%*.   ',
  '  =%@@+.  .-==-.  .+@@%=  ',
  ' +@@#.  -*%@@@@%*-  .#@@+ ',
  '=@@*   +@@@@@@@@@@+   *@@=',
  '#@@:  :@@@#-::-#@@@:  :@@#',
  '#@@:  :@@@:    :@@@:  :@@#',
  '=@@*   +@@@*::*@@@+   *@@=',
  ' +@@#.  -*%@@@@%*-  .#@@+ ',
  '  =%@@+.  .-==-.  .+@@%=  ',
  '   .*%@@#=-::::-=#@@%*.   ',
  '     :*%@@@@@@@@@%*:     ',
  '        .:*#%%#*:.        ',
].join('\n');

export class TitleScreen extends Screen {
  private resumeBtn: HTMLButtonElement;
  private quitBtn: HTMLButtonElement;
  private hasResume = false;

  constructor(emit: ActionSink, version: string, showQuit: boolean) {
    super('title', emit);

    const col = div('title-col');
    const mark = div('wordmark');
    const sigil = div('title-sigil');
    sigil.textContent = SIGIL;
    sigil.setAttribute('aria-hidden', 'true');
    mark.appendChild(sigil);
    mark.appendChild(div('wordmark-line', 'LUMEN'));
    mark.appendChild(div('wordmark-line', 'ABYSS'));
    col.appendChild(mark);

    const menu = div('title-menu');
    const descend = button('Descend', 'primary', 'play');
    descend.addEventListener('click', () => this.emit({ type: 'goto', screen: 'loadout' }));

    this.resumeBtn = button('Resume Run', '', 'refresh');
    this.resumeBtn.hidden = true;
    this.resumeBtn.addEventListener('click', () => this.emit({ type: 'goto', screen: 'resume-prompt' }));

    const archive = button('Archive', '', 'trophy');
    archive.addEventListener('click', () => this.emit({ type: 'goto', screen: 'archive' }));

    const settings = button('Settings', '', 'cog');
    settings.addEventListener('click', () => this.emit({ type: 'goto', screen: 'settings' }));

    this.quitBtn = button('Quit', '', 'times');
    this.quitBtn.hidden = !showQuit;
    this.quitBtn.addEventListener('click', () => this.emit({ type: 'quit' }));

    menu.appendChild(descend);
    menu.appendChild(this.resumeBtn);
    menu.appendChild(archive);
    menu.appendChild(settings);
    menu.appendChild(this.quitBtn);
    col.appendChild(menu);

    const strip = div('title-strip');
    strip.appendChild(div('version', 'v' + version));
    strip.appendChild(div('hint', 'any key = Descend'));
    col.appendChild(strip);

    this.root.appendChild(col);
    this.stops = [descend, this.resumeBtn, archive, settings, this.quitBtn];
  }

  setResumeAvailable(available: boolean): void {
    this.hasResume = available;
    setHidden(this.resumeBtn, !available);
  }

  override focusStops(): readonly HTMLElement[] {
    const out: HTMLElement[] = [];
    for (let i = 0; i < this.stops.length; i++) {
      if (!this.stops[i].hidden) out.push(this.stops[i]);
    }
    return out;
  }

  get resumeAvailable(): boolean {
    return this.hasResume;
  }
}
